import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, Preview } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import CustomTextField from "../../../components/ui/Textfield";
import CustomSelect from "../../../components/ui/Select";
import CompetencyQuestionEditor from "../../jobs/components/admin/CompetencyQuestionEditor";
import CompetencyTemplatePreview from "./components/CompetencyTemplatePreview";
import { DEPARTMENTS } from "../../jobs/constants";
import {
  createCompetencyTemplateAPI,
  deleteCompetencyTemplateAPI,
  getCompetencyTemplatesFromAPI,
  updateCompetencyTemplateAPI,
} from "../../jobs/services/competencyApi";
import type { CompetencyQuestion, CompetencyQuestionTemplate } from "../../jobs/types";
import { useCallback } from "react";
import { showErrorToast, showSuccessToast } from "../../../components/common/ShowToast";

const TAB_VALUES = ["all", ...DEPARTMENTS] as const;
type TabValue = (typeof TAB_VALUES)[number];

const CompetencyQuestionTemplates: React.FC = () => {
  const theme = useSelector((state: RootState) => state.theme);

  const [templates, setTemplates] = useState<CompetencyQuestionTemplate[]>([]);
  const [activeTab, setActiveTab] = useState<TabValue>("all");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CompetencyQuestionTemplate | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<CompetencyQuestionTemplate | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [name, setName] = useState("");
  const [department, setDepartment] = useState(DEPARTMENTS[0]);
  const [questions, setQuestions] = useState<CompetencyQuestion[]>([]);

  // Load templates on component mount
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCompetencyTemplatesFromAPI();
      setTemplates(data);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to load templates";
      showErrorToast(errorMsg, "competency-templates-load-error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = useMemo(() => {
    if (activeTab === "all") return templates;
    return templates.filter((t) => t.department === activeTab);
  }, [templates, activeTab]);

  const openCreateModal = () => {
    setEditingTemplate(null);
    setName("");
    setDepartment(DEPARTMENTS[0]);
    setQuestions([]);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template: CompetencyQuestionTemplate) => {
    setEditingTemplate(template);
    setName(template.name);
    setDepartment(template.department);
    setQuestions(template.questions);
    setIsModalOpen(true);
  };

  const handlePreview = (template: CompetencyQuestionTemplate) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setDeleteTargetId(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;

    try {
      setSubmitting(true);
      await deleteCompetencyTemplateAPI(deleteTargetId);
      showSuccessToast("Template deleted successfully");
      await loadTemplates();
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to delete template";
      showErrorToast(errorMsg, "competency-templates-delete-error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      showErrorToast("Template name is required", "competency-template-name-error");
      return;
    }

    if (questions.length === 0) {
      showErrorToast("Please add at least one question", "competency-template-question-error");
      return;
    }

    try {
      setSubmitting(true);
      const templateData = { name, department, questions };

      if (editingTemplate) {
        await updateCompetencyTemplateAPI(editingTemplate.id, templateData);
        showSuccessToast("Template updated successfully");
      } else {
        await createCompetencyTemplateAPI(templateData);
        showSuccessToast("Template created successfully");
      }

      await loadTemplates();
      setIsModalOpen(false);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to save template";
      showErrorToast(errorMsg, "competency-template-save-error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ color: theme.primary_colors[100], fontWeight: 600 }}>
          Competency Question Templates
        </Typography>
        <Button
          startIcon={<Add />}
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: theme.primary_colors[400],
            color: theme.primary_colors[900],
            "&:hover": { backgroundColor: theme.primary_colors[500] },
            "&:disabled": { opacity: 0.6 },
          }}
          onClick={openCreateModal}
        >
          Add New Template
        </Button>
      </Box>

      {/* Loading State */}
      {loading ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: theme.primary_colors[900],
            boxShadow: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: theme.primary_colors[400] }} />
          <Typography sx={{ color: theme.primary_colors[300] }}>
            Loading templates...
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Tabs */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue: TabValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                mb: 2,
                ".MuiTab-root": {
                  color: "text.secondary",
                  textTransform: "capitalize",
                  fontWeight: 600,
                },
                ".Mui-selected": { color: theme.primary_colors[400] },
              }}
              TabIndicatorProps={{
                style: { backgroundColor: theme.primary_colors[400] },
              }}
            >
              {TAB_VALUES.map((value) => (
                <Tab
                  key={value}
                  value={value}
                  label={value === "all" ? "All" : value}
                />
              ))}
            </Tabs>
          </Box>

          {/* Template Cards */}
          {filteredTemplates.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: "center",
                backgroundColor: theme.primary_colors[900],
                boxShadow: 0,
              }}
            >
              <Typography color={theme.primary_colors[300]}>
                {activeTab === "all"
                  ? `No templates yet. Click "Add New Template" to create your first one.`
                  : `No ${activeTab} templates yet.`}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {filteredTemplates.map((template) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
                  <Card
                    sx={{
                      backgroundColor: theme.primary_colors[900],
                      boxShadow: 0,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: theme.primary_colors[100],
                            fontWeight: 600,
                          }}
                        >
                          {template.name}
                        </Typography>
                        <Chip
                          label={template.department}
                          size="small"
                          sx={{
                            backgroundColor: theme.primary_colors[400],
                            color: theme.primary_colors[900],
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.primary_colors[300] }}
                      >
                        {template.questions.length} question
                        {template.questions.length === 1 ? "" : "s"}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(template)}
                        sx={{ color: theme.primary_colors[400] }}
                        title="Preview"
                      >
                        <Preview fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditTemplate(template)}
                        sx={{ color: theme.primary_colors[400] }}
                        title="Edit"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(template.id)}
                        sx={{ color: theme.primary_colors[400] }}
                        title="Delete"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Create / Edit Modal */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTemplate
            ? "Edit Template"
            : "New Competency Question Template"}
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}
          >
            <CustomTextField
              label="Template Name *"
              placeholder="e.g. Sales Default"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={submitting}
            />
            <CustomSelect
              label="Department"
              value={department}
              onChange={(e) => setDepartment(e.target.value as string)}
              disabled={submitting}
            >
              {DEPARTMENTS.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </CustomSelect>

            <CompetencyQuestionEditor
              questions={questions}
              onChange={setQuestions}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsModalOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: theme.primary_colors[400],
              color: theme.primary_colors[900],
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : editingTemplate ? (
              "Save Changes"
            ) : (
              "Create Template"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle sx={{ color: theme.primary_colors[100] }}>
          Delete Template
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.primary_colors[900] }}>
          <Typography sx={{ color: theme.primary_colors[400] }}>
            Are you sure you want to delete this template? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={submitting}
            sx={{ color: theme.primary_colors[300] }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: theme.primary_colors[400],
              color: theme.primary_colors[900],
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Modal */}
      <CompetencyTemplatePreview
        template={previewTemplate}
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewTemplate(null);
        }}
      />
    </Box>
  );
};

export default CompetencyQuestionTemplates;
